// The inbound ring the softphone plays while a call is alerting.
//
// WHY THIS EXISTS: onInvite set `status = "ringing"` and drew a card, and that
// was the whole notification. If the agent was on another tab, reading email, or
// simply looking away, the call rang out silently and the caller was dropped —
// which is exactly what "the call came but there was no ring sound" was. A desk
// phone's one non-negotiable feature is that it makes a noise.
//
// SYNTHESISED, NOT AN AUDIO FILE. A .wav/.mp3 asset would need bundling, a
// fetch, and a decode — all of which can fail or lag past the point the ring
// matters, and Vite would have to serve it under /ops. An oscillator starts in
// microseconds, adds no bytes to the bundle, and cannot 404.
//
// The cadence is the Indian standard (the desk this runs on, and what the agent
// expects to hear): 400 Hz, 0.4s on / 0.2s off / 0.4s on / 2.0s off.

/** 400 Hz — Indian ringing tone. */
const FREQ_HZ = 400;
/** on, off, on, off — one full cadence period, in seconds. */
const CADENCE = [0.4, 0.2, 0.4, 2.0];
const PERIOD = CADENCE.reduce((a, b) => a + b, 0);
/** Comfortably longer than the PBX's 30s Dial timeout, so the ring never
 *  stops before the call does. */
const MAX_SECONDS = 120;

let ctx: AudioContext | null = null;
let osc: OscillatorNode | null = null;
let gain: GainNode | null = null;

function audioCtor(): typeof AudioContext | undefined {
  return window.AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
}

// Chrome starts an AudioContext SUSPENDED unless the page has seen a user
// gesture, and resume() from inside an incoming SIP INVITE is not a gesture —
// so the first call after a fresh page load would ring silently, which is the
// one ring that matters most. Creating and resuming the context on the agent's
// first click/keypress means it is already running when a call arrives.
//
// `once` + capture so it costs a single listener and fires even if something
// downstream stops propagation.
// KEEP TRYING UNTIL THE CONTEXT IS ACTUALLY RUNNING.
//
// This used to be `{ once: true }`, which spends the listener on the first
// click whether or not the resume succeeded. resume() is async and can be
// refused — the gesture may not be one the browser accepts, the tab may be
// backgrounded, the context may not exist yet — and once the listener is gone
// there is no second chance for the rest of the session. An agent whose first
// click lands at the wrong moment then has a phone that never makes a sound.
//
// So the listener removes itself only once `ctx.state === "running"`, which is
// the only evidence that audio will actually play.
if (typeof window !== "undefined") {
  const EVENTS = ["pointerdown", "keydown"] as const;
  const prime = () => {
    try {
      const Ctor = audioCtor();
      if (!Ctor) return;
      ctx = ctx ?? new Ctor();
      void ctx.resume().then(() => {
        if (ctx?.state === "running") {
          for (const ev of EVENTS) window.removeEventListener(ev, prime, true);
        }
      }).catch(() => { /* keep the listener; try again on the next gesture */ });
    } catch { /* no audio in this browser — the visual alert still shows */ }
  };
  for (const ev of EVENTS) window.addEventListener(ev, prime, { capture: true });
}

/** True when the browser is still refusing to play audio, so a ring would be
 *  silent. The desk uses this to say so rather than let a call arrive
 *  unannounced. */
export function ringerBlocked(): boolean {
  return ctx != null && ctx.state !== "running";
}

// A VISIBLE RING, FOR WHEN THERE IS NO AUDIBLE ONE.
//
// Autoplay policy can silence the tone on a freshly loaded page, and an agent
// looking at another window has nothing at all to tell them a customer is
// waiting. Flashing the tab title needs no permission, survives being muted,
// and is visible in the taskbar — so the alert degrades instead of vanishing.
let titleTimer: number | null = null;
let savedTitle = "";
function startTitleFlash(): void {
  if (typeof document === "undefined" || titleTimer != null) return;
  savedTitle = document.title;
  let on = false;
  titleTimer = window.setInterval(() => {
    on = !on;
    document.title = on ? "📞 Incoming call" : savedTitle;
  }, 900);
}
function stopTitleFlash(): void {
  if (titleTimer == null) return;
  window.clearInterval(titleTimer);
  titleTimer = null;
  if (typeof document !== "undefined" && savedTitle) document.title = savedTitle;
}

/**
 * Start ringing. Safe to call when already ringing (no-op) and safe to call
 * when the browser forbids audio — it resolves either way and never throws
 * into the SIP delegate, where an exception would break call handling.
 */
export function startRing(): void {
  if (osc) return; // already ringing
  // Before the tone, and regardless of whether the tone is allowed to play.
  startTitleFlash();
  try {
    const Ctor = audioCtor();
    if (!Ctor) return;
    ctx = ctx ?? new Ctor();

    // Autoplay policy suspends a context created without a user gesture. The
    // agent has signed in and granted the mic by this point, so resume normally
    // succeeds; if it does not, we lose the sound but not the call.
    void ctx.resume().catch(() => {});

    const c = ctx;
    osc = c.createOscillator();
    gain = c.createGain();
    osc.frequency.value = FREQ_HZ;
    osc.type = "sine";
    osc.connect(gain);
    gain.connect(c.destination);

    // Schedule the whole cadence up front against the audio clock rather than
    // driving it from setInterval: a timer drifts, and in a background tab it is
    // throttled to once a second — which would mangle the ring precisely when
    // the agent most needs to hear it.
    const t0 = c.currentTime;
    gain.gain.setValueAtTime(0, t0);
    for (let n = 0; n * PERIOD < MAX_SECONDS; n++) {
      const base = t0 + n * PERIOD;
      let at = base;
      for (let i = 0; i < CADENCE.length; i++) {
        const on = i % 2 === 0;
        // Ramp rather than switch: an instant gain step is an audible click.
        gain.gain.setValueAtTime(on ? 0 : 0.18, at);
        gain.gain.linearRampToValueAtTime(on ? 0.18 : 0, at + 0.02);
        at += CADENCE[i];
      }
    }
    osc.start(t0);
    osc.stop(t0 + MAX_SECONDS);
  } catch {
    // No audio available — the visual alert is still shown.
    osc = null;
    gain = null;
  }
}

/** Stop ringing. Must be called on EVERY path out of the alerting state —
 *  answered, rejected, caller gave up, or the agent navigated away. */
export function stopRing(): void {
  stopTitleFlash();
  try { osc?.stop(); } catch { /* already stopped */ }
  try { osc?.disconnect(); gain?.disconnect(); } catch { /* ignore */ }
  osc = null;
  gain = null;
}
